// DESCRIPTION: Week view — 7 time-slot columns, events absolutely positioned by start time + duration.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import EventPopover from './EventPopover';
import EventCell from './EventCell';
import {
	formatHourLabel,
	formatTime,
	getDisplayTime,
	isMultiDayEvent,
	getEventSpanDates,
	computeOverlapColumns,
} from './utils';

const config = window.nemtlyCalendar || {};
const strings = config.strings || {};

// Hours displayed in the gutter (0–23).
const HOURS = Array.from( { length: 24 }, ( _, i ) => i );

/**
 * Given a time string "HH:MM" and duration in minutes, return
 * { top, height } as percentages of the total day height (24 * CELL_HEIGHT px).
 *
 * We use CSS custom properties set inline so the event block can be absolutely
 * positioned within its column.
 */
const CELL_HEIGHT_PX = 56; // matches nb-h-14 = 3.5rem = 56px

function getPositionStyle( time, durationMinutes ) {
	if ( ! time ) {
		return {};
	}
	const [ h, m ] = time.split( ':' ).map( Number );
	const startMin = h * 60 + ( m || 0 );
	const topPx = ( startMin / 60 ) * CELL_HEIGHT_PX;
	const heightPx = Math.max(
		( ( durationMinutes || 60 ) / 60 ) * CELL_HEIGHT_PX,
		20
	);
	return { top: `${ topPx }px`, height: `${ heightPx }px` };
}

/**
 * Get the 7 column dates for the week given dateFrom.
 *
 * @param {string} dateFrom ISO date string.
 * @return {Date[]}
 */
function getWeekDates( dateFrom ) {
	return Array.from( { length: 7 }, ( _, i ) => {
		const d = new Date( dateFrom );
		d.setDate( d.getDate() + i );
		return d;
	} );
}

const DAY_SHORT = [ 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat' ];

const SWIPE_THRESHOLD = 50; // minimum px to register as a swipe

/**
 * Hook that returns true when the viewport width is below the mobile breakpoint.
 *
 * @param {number} breakpoint Pixel width threshold.
 *
 * @return {boolean}
 */
function useIsMobile( breakpoint = 768 ) {
	const [ isMobile, setIsMobile ] = useState(
		typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
	);

	useEffect( () => {
		const mql = window.matchMedia( `(max-width: ${ breakpoint - 1 }px)` );
		const handler = ( e ) => setIsMobile( e.matches );
		mql.addEventListener( 'change', handler );
		setIsMobile( mql.matches );
		return () => mql.removeEventListener( 'change', handler );
	}, [ breakpoint ] );

	return isMobile;
}

/**
 * WeekView
 *
 * @param {Object}  props
 * @param {Array}   props.events
 * @param {Date}    props.anchor
 * @param {string}  props.dateFrom
 * @param {boolean} props.useLocalTz Whether to show times in browser's local timezone.
 */
export default function WeekView( { events, anchor, dateFrom, useLocalTz } ) {
	const [ activeEvent, setActiveEvent ] = useState( null );
	const [ mobileDayIdx, setMobileDayIdx ] = useState( 0 );
	const touchStartX = useRef( 0 );
	const isMobile = useIsMobile();
	const weekDates = getWeekDates( dateFrom );

	// Reset mobile day index when dateFrom changes (user navigated weeks).
	useEffect( () => {
		setMobileDayIdx( 0 );
	}, [ dateFrom ] );

	const handleTouchStart = useCallback( ( e ) => {
		touchStartX.current = e.touches[ 0 ].clientX;
	}, [] );

	const handleTouchEnd = useCallback( ( e ) => {
		const delta = touchStartX.current - e.changedTouches[ 0 ].clientX;
		if ( Math.abs( delta ) < SWIPE_THRESHOLD ) {
			return;
		}

		// In RTL, swipe directions are reversed.
		const isRTL = document.documentElement.dir === 'rtl';
		const goForward = isRTL ? delta < 0 : delta > 0;

		if ( goForward ) {
			// Swipe toward next day
			setMobileDayIdx( ( prev ) => Math.min( prev + 1, 6 ) );
		} else {
			// Swipe toward previous day
			setMobileDayIdx( ( prev ) => Math.max( prev - 1, 0 ) );
		}
	}, [] );

	const weekDateStrs = weekDates.map( ( d ) =>
		d.toISOString().slice( 0, 10 )
	);
	const weekStart = weekDateStrs[ 0 ];
	const weekEnd = weekDateStrs[ 6 ];

	// Separate events into: all-day (no time), multi-day (timed but spans midnight), and single-day timed.
	const allDaySingle = []; // no time, single-day
	const multiDay = []; // timed but spans multiple days
	const timedByDate = {};

	events.forEach( ( event ) => {
		if ( ! event.time ) {
			allDaySingle.push( event );
		} else if ( isMultiDayEvent( event ) ) {
			multiDay.push( event );
		} else {
			const key = event.date_utc || event.date || '';
			if ( ! timedByDate[ key ] ) {
				timedByDate[ key ] = [];
			}
			timedByDate[ key ].push( event );
		}
	} );

	// Build spanning data for multi-day events in the all-day row.
	const multiDaySpans = [];
	multiDay.forEach( ( event ) => {
		const spanDates = getEventSpanDates( event, weekStart, weekEnd );
		if ( spanDates.length === 0 ) {
			return;
		}
		const firstIdx = weekDateStrs.indexOf( spanDates[ 0 ] );
		const lastIdx = weekDateStrs.indexOf(
			spanDates[ spanDates.length - 1 ]
		);
		if ( firstIdx < 0 || lastIdx < 0 ) {
			return;
		}

		multiDaySpans.push( {
			event,
			colStart: firstIdx + 1,
			colEnd: lastIdx + 2,
		} );
	} );

	// Index single all-day events by date for non-spanning rendering.
	const allDayByDate = {};
	allDaySingle.forEach( ( event ) => {
		const key = event.date_utc || event.date || '';
		if ( ! allDayByDate[ key ] ) {
			allDayByDate[ key ] = [];
		}
		allDayByDate[ key ].push( event );
	} );

	const hasAnyBanner =
		multiDaySpans.length > 0 || Object.keys( allDayByDate ).length > 0;
	const totalHeight = HOURS.length * CELL_HEIGHT_PX;

	// ── Mobile: single-day swipeable view ──────────────────────────────
	if ( isMobile ) {
		const mobileDate = weekDates[ mobileDayIdx ];
		const mobileDateStr = mobileDate.toISOString().slice( 0, 10 );
		const mobileTimed = timedByDate[ mobileDateStr ] || [];
		const mobileAllDay = allDayByDate[ mobileDateStr ] || [];
		const mobileMulti = multiDay.filter( ( event ) => {
			const spans = getEventSpanDates(
				event,
				mobileDateStr,
				mobileDateStr
			);
			return spans.length > 0;
		} );
		const isToday =
			mobileDateStr === new Date().toISOString().slice( 0, 10 );

		return (
			<div
				className="nb-calendar-week-mobile"
				onTouchStart={ handleTouchStart }
				onTouchEnd={ handleTouchEnd }
			>
				{ /* Day selector strip */ }
				<div className="nb-week-mobile__day-strip">
					{ weekDates.map( ( date, idx ) => {
						const dateStr = date.toISOString().slice( 0, 10 );
						const active = idx === mobileDayIdx;
						const today =
							dateStr === new Date().toISOString().slice( 0, 10 );

						return (
							<button
								key={ dateStr }
								className={ [
									'nb-week-mobile__day-tab',
									active
										? 'nb-week-mobile__day-tab--active'
										: '',
									today
										? 'nb-week-mobile__day-tab--today'
										: '',
								]
									.filter( Boolean )
									.join( ' ' ) }
								onClick={ () => setMobileDayIdx( idx ) }
							>
								<span className="nb-week-mobile__day-name">
									{ DAY_SHORT[ date.getDay() ] }
								</span>
								<span className="nb-week-mobile__day-num">
									{ date.getDate() }
								</span>
							</button>
						);
					} ) }
				</div>

				{ /* All-day events for this day */ }
				{ ( mobileAllDay.length > 0 || mobileMulti.length > 0 ) && (
					<div className="nb-week-mobile__all-day">
						{ [ ...mobileMulti, ...mobileAllDay ].map(
							( event ) => (
								<EventCell
									key={ event.id }
									event={ event }
									isAllDay
									useLocalTz={ useLocalTz }
								/>
							)
						) }
					</div>
				) }

				{ /* Time grid for single day */ }
				<div
					className="nb-calendar-time-grid"
					style={ { minHeight: '400px' } }
				>
					<div className="nb-calendar-time-gutter">
						{ HOURS.map( ( h ) => (
							<div
								key={ h }
								className="nb-calendar-time-label"
								style={ { height: `${ CELL_HEIGHT_PX }px` } }
							>
								{ formatHourLabel( h ) }
							</div>
						) ) }
					</div>

					<div className="nb-calendar-time-columns">
						<div className="nb-calendar-time-column">
							<div
								style={ {
									position: 'relative',
									height: `${ totalHeight }px`,
								} }
							>
								{ HOURS.map( ( h ) => (
									<div
										key={ h }
										className="nb-calendar-time-slot"
										style={ {
											height: `${ CELL_HEIGHT_PX }px`,
										} }
									/>
								) ) }

								{ mobileTimed.map( ( event ) => (
									<button
										key={ event.id }
										className={ `nb-calendar-time-event${
											event.is_past
												? ' nb-calendar-time-event--past'
												: ''
										}` }
										style={ getPositionStyle(
											event.time,
											event.duration
										) }
										onClick={ () =>
											setActiveEvent(
												activeEvent?.id === event.id
													? null
													: event
											)
										}
										aria-label={ event.title }
									>
										<div className="nb-calendar-time-event__title">
											{ event.title }
										</div>
										{ config.showEventTime !== false &&
											event.time && (
												<div className="nb-calendar-time-event__time">
													{ formatTime(
														getDisplayTime(
															event,
															useLocalTz
														)
													) }
												</div>
											) }
										{ activeEvent?.id === event.id && (
											<EventPopover
												event={ event }
												onClose={ () =>
													setActiveEvent( null )
												}
											/>
										) }
									</button>
								) ) }
							</div>
						</div>
					</div>
				</div>

				{ /* Swipe hint */ }
				<p className="nb-week-mobile__swipe-hint">
					{ strings.swipeHint || 'Swipe left/right to change day' }
				</p>
			</div>
		);
	}

	// ── Desktop: full 7-column layout ──────────────────────────────────
	return (
		<div>
			{ /* All-day / multi-day banner row */ }
			{ hasAnyBanner && (
				<div className="nb-calendar-all-day-row">
					<div className="nb-calendar-all-day-label">All day</div>
					<div className="nb-calendar-all-day-cells">
						{ /* Multi-day spanning events */ }
						{ multiDaySpans.length > 0 && (
							<div className="nb-calendar-spanning-layer nb-calendar-spanning-layer--week">
								{ multiDaySpans.map( ( span ) => (
									<EventCell
										key={ span.event.id }
										event={ span.event }
										isAllDay
										useLocalTz={ useLocalTz }
										spanStyle={ {
											gridColumn: `${ span.colStart } / ${ span.colEnd }`,
										} }
										spanClassName="nb-calendar-spanning-event"
									/>
								) ) }
							</div>
						) }

						{ /* Single all-day events per column */ }
						{ weekDates.map( ( date ) => {
							const dateStr = date.toISOString().slice( 0, 10 );
							const allDayEvts = allDayByDate[ dateStr ] || [];
							return (
								<div
									key={ dateStr }
									className="nb-calendar-all-day-cell"
								>
									{ allDayEvts.map( ( event ) => (
										<EventCell
											key={ event.id }
											event={ event }
											isAllDay
											useLocalTz={ useLocalTz }
										/>
									) ) }
								</div>
							);
						} ) }
					</div>
				</div>
			) }

			<div
				className="nb-calendar-time-grid"
				style={ { minHeight: '400px' } }
			>
				{ /* Time gutter */ }
				<div className="nb-calendar-time-gutter">
					<div style={ { height: `${ CELL_HEIGHT_PX }px` } } />{ ' ' }
					{ /* header spacer */ }
					{ HOURS.map( ( h ) => (
						<div
							key={ h }
							className="nb-calendar-time-label"
							style={ { height: `${ CELL_HEIGHT_PX }px` } }
						>
							{ formatHourLabel( h ) }
						</div>
					) ) }
				</div>

				{ /* Day columns */ }
				<div className="nb-calendar-time-columns">
					{ weekDates.map( ( date ) => {
						const dateStr = date.toISOString().slice( 0, 10 );
						const dayEvents = timedByDate[ dateStr ] || [];
						const isToday =
							dateStr === new Date().toISOString().slice( 0, 10 );
						const layoutEvents = computeOverlapColumns( dayEvents );

						return (
							<div
								key={ dateStr }
								className="nb-calendar-time-column"
							>
								{ /* Column header */ }
								<div
									className="nb-calendar-time-column-header"
									style={ {
										height: `${ CELL_HEIGHT_PX }px`,
										fontWeight: isToday ? 'bold' : 'normal',
									} }
								>
									<div>{ DAY_SHORT[ date.getDay() ] }</div>
									<div>{ date.getDate() }</div>
								</div>

								{ /* Time slots (background grid) */ }
								<div
									style={ {
										position: 'relative',
										height: `${ totalHeight }px`,
									} }
								>
									{ HOURS.map( ( h ) => (
										<div
											key={ h }
											className="nb-calendar-time-slot"
											style={ {
												height: `${ CELL_HEIGHT_PX }px`,
											} }
										/>
									) ) }

									{ /* Absolutely positioned events — side-by-side when overlapping */ }
									{ layoutEvents.map(
										( { event, column, totalColumns } ) => {
											const posStyle = getPositionStyle(
												event.time,
												event.duration
											);
											const widthPct = 100 / totalColumns;
											const leftPct = column * widthPct;

											return (
												<button
													key={ event.id }
													className={ `nb-calendar-time-event${
														event.is_past
															? ' nb-calendar-time-event--past'
															: ''
													}` }
													style={ {
														...posStyle,
														left: `calc(${ leftPct }% + 2px)`,
														width: `calc(${ widthPct }% - 4px)`,
														right: 'auto',
													} }
													onClick={ () =>
														setActiveEvent(
															activeEvent?.id ===
																event.id
																? null
																: event
														)
													}
													aria-label={ event.title }
												>
													<div className="nb-calendar-time-event__title">
														{ event.title }
													</div>
													{ config.showEventTime !==
														false &&
														event.time && (
															<div className="nb-calendar-time-event__time">
																{ formatTime(
																	getDisplayTime(
																		event,
																		useLocalTz
																	)
																) }
															</div>
														) }
													{ activeEvent?.id ===
														event.id && (
														<EventPopover
															event={ event }
															onClose={ () =>
																setActiveEvent(
																	null
																)
															}
														/>
													) }
												</button>
											);
										}
									) }
								</div>
							</div>
						);
					} ) }
				</div>
			</div>
		</div>
	);
}
